import { MartTimeSeriesPoint } from 'src/redux/admin/mart-reporting/mart-reporting.types';

export const BETA_TIME_SERIES_METRICS = [
  { key: 'clicks', label: 'Clicks', colour: '#F59E0B' },
  { key: 'conversions', label: 'Conversions', colour: '#2563EB' },
  { key: 'impressions', label: 'Impressions', colour: '#059669' },
  { key: 'spend', label: 'Spend', colour: '#EF4444' },
] as const;

export type BetaTimeSeriesMetricKey = (typeof BETA_TIME_SERIES_METRICS)[number]['key'];

export type TimeSeriesScaleMode = 'single' | 'dual' | 'normalized' | 'hidden';

const SCALE_RATIO_THRESHOLD = 15;

export type ProcessedTimeSeriesPoint = MartTimeSeriesPoint & {
  dateLabel: string;
  originalValues: Record<string, number>;
};

const metricValue = (point: MartTimeSeriesPoint, key: string): number => {
  const value = Number(point[key] ?? 0);
  return Number.isFinite(value) ? value : 0;
};

export const getMetricMax = (data: MartTimeSeriesPoint[], key: string): number => {
  if (!data.length) return 0;
  return Math.max(...data.map((point) => metricValue(point, key)));
};

export const formatMetricAxisValue = (value: number, key: string): string => {
  if (!Number.isFinite(value)) return '0';
  if (key === 'spend') {
    if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
    if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}k`;
    return `$${value.toFixed(0)}`;
  }
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(0)}k`;
  return value.toLocaleString(undefined, { maximumFractionDigits: 0 });
};

export const formatMetricTooltipValue = (value: number, key: string): string => {
  if (!Number.isFinite(value)) return '0';
  if (key === 'spend') {
    return value.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
  }
  return value.toLocaleString(undefined, { maximumFractionDigits: 0 });
};

export function computeScaleMode(
  visibleKeys: BetaTimeSeriesMetricKey[],
  data: MartTimeSeriesPoint[],
): TimeSeriesScaleMode {
  if (visibleKeys.length === 0) return 'hidden';
  if (visibleKeys.length === 1) return 'single';

  const maxes = visibleKeys.map((key) => getMetricMax(data, key)).filter((value) => value > 0);
  if (!maxes.length) return 'single';

  const ratio = Math.max(...maxes) / Math.min(...maxes);

  if (visibleKeys.length === 2 && ratio > SCALE_RATIO_THRESHOLD) {
    return 'dual';
  }
  if (visibleKeys.length >= 3) {
    return ratio > SCALE_RATIO_THRESHOLD ? 'hidden' : 'normalized';
  }
  if (ratio > SCALE_RATIO_THRESHOLD) {
    return 'normalized';
  }
  return 'single';
}

export function buildProcessedChartData(
  data: MartTimeSeriesPoint[],
  visibleKeys: BetaTimeSeriesMetricKey[],
  scaleMode: TimeSeriesScaleMode,
): ProcessedTimeSeriesPoint[] {
  const ranges = Object.fromEntries(
    visibleKeys.map((key) => {
      const values = data.map((point) => metricValue(point, key));
      const min = values.length ? Math.min(...values) : 0;
      const max = values.length ? Math.max(...values) : 0;
      return [key, { min, max }];
    }),
  ) as Record<string, { min: number; max: number }>;

  return data.map((point) => {
    const originalValues = Object.fromEntries(
      visibleKeys.map((key) => [key, metricValue(point, key)]),
    ) as Record<string, number>;

    const processed: ProcessedTimeSeriesPoint = {
      ...point,
      dateLabel: point.date,
      originalValues,
    };

    if (scaleMode !== 'normalized' && scaleMode !== 'hidden') {
      return processed;
    }

    visibleKeys.forEach((key) => {
      const value = originalValues[key];
      const { min, max } = ranges[key];
      const span = max - min;
      processed[key] = span > 0 ? ((value - min) / span) * 100 : 50;
    });

    return processed;
  });
}

export function assignDualAxes(visibleKeys: BetaTimeSeriesMetricKey[], data: MartTimeSeriesPoint[]) {
  const sorted = [...visibleKeys].sort(
    (left, right) => getMetricMax(data, left) - getMetricMax(data, right),
  );
  return {
    leftKey: sorted[0],
    rightKey: sorted[1],
  };
}

export function scaleModeDescription(
  scaleMode: TimeSeriesScaleMode,
  visibleKeys: BetaTimeSeriesMetricKey[],
  data: MartTimeSeriesPoint[],
): string | null {
  const labelFor = (key: BetaTimeSeriesMetricKey) =>
    BETA_TIME_SERIES_METRICS.find((metric) => metric.key === key)?.label ?? key;

  if (scaleMode === 'dual' && visibleKeys.length === 2) {
    const { leftKey, rightKey } = assignDualAxes(visibleKeys, data);
    return `${labelFor(leftKey)} uses the left axis · ${labelFor(rightKey)} uses the right axis`;
  }
  if (scaleMode === 'normalized') {
    return 'Each metric is normalised to its own range (0–100%) for comparison';
  }
  if (scaleMode === 'hidden') {
    return 'Metrics use independent scales — axis labels hidden when ranges differ too widely';
  }
  if (scaleMode === 'single' && visibleKeys.length > 1) {
    return 'Shared scale — selected metrics are comparable on one axis';
  }
  return null;
}
